import { Card, ColorInput, Group, SimpleGrid, Stack, Text, TextInput, Textarea } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useEffect } from 'react'

/**
 * Format phone number to (XXX) XXX-XXXX format
 */
const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '')
  
  // Limit to 10 digits
  const limitedDigits = digits.slice(0, 10)
  
  // Format based on length
  if (limitedDigits.length === 0) return ''
  if (limitedDigits.length <= 3) return `(${limitedDigits}`
  if (limitedDigits.length <= 6) return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`
  return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`
}

interface GroupBusinessBasicsData {
  groupName: string
  groupDescription?: string
  headquarters?: {
    address?: {
      line1?: string
      line2?: string
      city?: string
      state?: string
      zipCode?: string
    }
    primaryPhone?: string
    email?: string
    website?: string
  }
  brandingGuidelines?: {
    logoUrl?: string
    primaryColor?: string
    secondaryColor?: string
  }
  timezone?: string
  corporateContacts?: Array<{
    name: string
    email: string
    phone: string
    title: string
  }>
}

interface GroupBusinessBasicsFormProps {
  data: GroupBusinessBasicsData | null
  onSave: (data: GroupBusinessBasicsData) => Promise<void>
  onFormReady: (form: { reset: () => void; submit: () => void }) => void
}

export function GroupBusinessBasicsForm({ data, onSave, onFormReady }: GroupBusinessBasicsFormProps) {
  const form = useForm<GroupBusinessBasicsData>({
    initialValues: {
      groupName: '',
      groupDescription: '',
      headquarters: {
        address: {
          line1: '',
          line2: '',
          city: '',
          state: '',
          zipCode: '',
        },
        primaryPhone: '',
        email: '',
        website: '',
      },
      brandingGuidelines: {
        logoUrl: '',
        primaryColor: '#003087',
        secondaryColor: '#FFB81C',
      },
      timezone: 'America/Chicago',
    },
    validate: {
      groupName: (value) => (value?.trim() ? null : 'Group name is required'),
      headquarters: {
        email: (value) => {
          if (!value) return null
          return /^\S+@\S+\.\S+$/.test(value) ? null : 'Invalid email address'
        },
      },
      brandingGuidelines: {
        primaryColor: (value) => {
          if (!value) return null
          return /^#[0-9A-Fa-f]{6}$/.test(value) ? null : 'Must be 6-digit hex color (e.g., #FFFFFF)'
        },
        secondaryColor: (value) => {
          if (!value) return null
          return /^#[0-9A-Fa-f]{6}$/.test(value) ? null : 'Must be 6-digit hex color (e.g., #FFFFFF)'
        },
      },
    },
  })

  // Update form when data changes
  useEffect(() => {
    if (data) {
      form.setValues(data)
    }
  }, [data])

  // Expose form methods to parent
  useEffect(() => {
    onFormReady({
      reset: handleReset,
      submit: () => form.onSubmit(handleSubmit)()
    })
  }, [onFormReady])

  const handleSubmit = async (values: GroupBusinessBasicsData) => {
    await onSave(values)
  }

  const handleReset = () => {
    if (data) {
      form.setValues(data)
    } else {
      form.reset()
    }
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
        {/* Left Column - Business Basics */}
        <Card shadow="sm" padding="xl" radius="md" withBorder>
          <Stack gap="lg">
            <div>
              <Text size="sm" fw={600} mb="xs">
                Group Information
              </Text>
              <Stack gap="md">
                <TextInput
                  label="Group Name"
                  placeholder="Enter dealership group name"
                  required
                  {...form.getInputProps('groupName')}
                />

                <Textarea
                  label="Description"
                  placeholder="Brief description of the dealership group"
                  rows={3}
                  {...form.getInputProps('groupDescription')}
                />
              </Stack>
            </div>

            <div>
              <Text size="sm" fw={600} mb="xs">
                Headquarters Address
              </Text>
              <Stack gap="md">
                <TextInput
                  label="Address Line 1"
                  placeholder="Street address"
                  {...form.getInputProps('headquarters.address.line1')}
                />

                <TextInput
                  label="Address Line 2"
                  placeholder="Suite, unit, building, floor, etc."
                  {...form.getInputProps('headquarters.address.line2')}
                />

                <Group grow>
                  <TextInput label="City" placeholder="City" {...form.getInputProps('headquarters.address.city')} />
                  <TextInput
                    label="State"
                    placeholder="State"
                    maxLength={2}
                    {...form.getInputProps('headquarters.address.state')}
                  />
                  <TextInput
                    label="ZIP Code"
                    placeholder="ZIP"
                    maxLength={10}
                    {...form.getInputProps('headquarters.address.zipCode')}
                  />
                </Group>
              </Stack>
            </div>

            <div>
              <Text size="sm" fw={600} mb="xs">
                Contact Information
              </Text>
              <Stack gap="md">
                <TextInput
                  label="Primary Phone"
                  placeholder="(555) 123-4567"
                  maxLength={14}
                  value={form.values.headquarters?.primaryPhone || ''}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value)
                    form.setFieldValue('headquarters.primaryPhone', formatted)
                  }}
                  onBlur={() => form.validateField('headquarters.primaryPhone')}
                  error={(form.errors.headquarters as any)?.primaryPhone}
                />

                <TextInput
                  label="Email"
                  type="email"
                  placeholder="contact@dealershipgroup.com"
                  {...form.getInputProps('headquarters.email')}
                />

                <TextInput
                  label="Website"
                  type="url"
                  placeholder="https://www.dealershipgroup.com"
                  {...form.getInputProps('headquarters.website')}
                />
              </Stack>
            </div>
          </Stack>
        </Card>

        {/* Right Column - Branding Guidelines */}
        <Card shadow="sm" padding="xl" radius="md" withBorder>
          <Stack gap="lg">
            <div>
              <Text size="sm" fw={600} mb="xs">
                Branding Guidelines
              </Text>
              <Stack gap="md">
                <TextInput
                  label="Logo URL"
                  placeholder="https://example.com/logo.png"
                  {...form.getInputProps('brandingGuidelines.logoUrl')}
                />

                <Group grow>
                  <ColorInput
                    label="Primary Brand Color"
                    placeholder="Pick a color"
                    format="hex"
                    {...form.getInputProps('brandingGuidelines.primaryColor')}
                  />

                  <ColorInput
                    label="Secondary Brand Color"
                    placeholder="Pick a color"
                    format="hex"
                    {...form.getInputProps('brandingGuidelines.secondaryColor')}
                  />
                </Group>
              </Stack>
            </div>
          </Stack>
        </Card>
      </SimpleGrid>
    </form>
  )
}

